/**
 * @file StraightsPricing.js
 * @version 2.21.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2025-11-04
 * @modified 2026-10-28
 * @description Straights Pricing — list of parent contracts, each expanding to
 *              show its per-month child rows. Uses the new
 *              pricing.straights_contracts data model.
 */

import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { API_URL } from '../config';
import { setStraightsContractFulfilled, fulfilledAction } from '../utils/contractFulfilment';
import { isPastPeriod, periodSortKey } from '../utils/periodHelper';
import { formatQuantity } from '../utils/numberFormatters';
import './SearchLayout.css';
import './ContractPricing.css';
import './ResizableColumns.css';
import './StraightsPricing.css';
import Dropdown from './Dropdown';
import Input from './Input';
import MultiSelectDropdown from './MultiSelectDropdown';
import PaginationControls from './PaginationControls';
import LoadingSpinner from './LoadingSpinner';
import AlertDialog from './AlertDialog';
import ConfirmDialog from './ConfirmDialog';
import ContactModal from './ContactModal';
import StraightsContractModal from './StraightsContractModal';
import RecalculateQuantitiesButton from './RecalculateQuantitiesButton';
import RefreshAutoLinksButton from './RefreshAutoLinksButton';
import ContractOrdersModal from './ContractOrdersModal';
import { formatPrice } from '../utils/currencyFormatter';
import { aggregateFulfilmentStatus } from '../utils/pricingHelpers';
import { LinkedPosBadge, ContractCustomerCell, ProductCell } from './PricingBadges';
import RowActionsMenu from './RowActionsMenu';
import { openStraightsContractPdf } from '../utils/contractPdf';
import TableContextMenu from './TableContextMenu';
import { useSlideOutButton } from './SlideOutButton';
import usePersistedState from '../hooks/usePersistedState';
import useResizableColumns from '../hooks/useResizableColumns';
import useTableContextMenu from '../hooks/useTableContextMenu';
import { matchesAllTerms, searchFieldsSummary } from '../utils/searchMatch';
import SearchHelp from './SearchHelp';
import { activeHeaderFilters, clearFiltersClass } from '../utils/activeFilters';

const MONTH_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

// Columns the free-text search box looks at; narrowable via the field-selector
// dropdown beside the search input. Contract # spans every month's numbers.
const SEARCH_FIELDS = [
  { key: 'customer', label: 'Customer', get: c => c.company_name },
  { key: 'account', label: 'Account', get: c => c.account_no },
  { key: 'product', label: 'Product', get: c => c.product_name },
  { key: 'code', label: 'Product Code', get: c => c.product_code },
  {
    /*
     * Everything on the parent and everything under it. This used to reach
     * only the months' legacy contract_numbers, which left out both of the
     * numbers people actually search by: the parent's own contract_number —
     * printed in the Contract column of the row you are looking at — and each
     * month's supplier_contract_number. Precedence between the two month
     * fields matters when DISPLAYING one (see StraightsContractModal); for
     * searching, everything the screen holds should be reachable.
     */
    key: 'contract',
    label: 'Contract #',
    get: c => [
      c.contract_number,
      ...(c.months || []).flatMap(m => [
        m.supplier_contract_number,
        ...(Array.isArray(m.contract_numbers) ? m.contract_numbers : (m.contract_number ? [m.contract_number] : [])),
      ]),
    ].filter(Boolean).join(' '),
  },
];
const ALL_SEARCH_KEYS = SEARCH_FIELDS.map(f => f.key);
const SEARCH_FIELD_OPTIONS = SEARCH_FIELDS.map(f => ({ value: f.key, label: f.label }));

const fmtMonthYear = (year, month) =>
  `${MONTH_SHORT[(parseInt(month, 10) || 1) - 1]} ${year}`;

const sumQty = (months) =>
  (months || []).reduce((acc, m) => acc + (parseFloat(m.quantity) || 0), 0);

const sumRemaining = (months) =>
  (months || []).reduce((acc, m) => acc + (parseFloat(m.remaining_quantity) || 0), 0);

const dateRange = (months) => {
  if (!months || months.length === 0) return { start: null, end: null };
  const sorted = [...months].sort((a, b) => (a.year - b.year) || (a.month - b.month));
  return { start: sorted[0], end: sorted[sorted.length - 1] };
};

/*
 * What Clear Filters resets to — and so what "no filters applied" means for
 * the button's highlight (utils/activeFilters.js).
 */
const CLEARED_FILTERS = {
  search: '', period: '', supplier: '', delivery_terms: '',
  limit: '50', showArchived: false, showContracted: true
};
const FILTER_LABELS = { showArchived: 'Archived', showContracted: 'Contracted' };

function StraightsPricing({ user }) {
  const [contracts, setContracts] = useState([]);
  const [periods, setPeriods] = useState([]);
  // "Add orders…" on a row: { set, title } (ContractOrdersModal, 28 Oct 2026).
  const [ordersFor, setOrdersFor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [selectedIds, setSelectedIds] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [modalMode, setModalMode] = useState('edit');
  const [selectedContactId, setSelectedContactId] = usePersistedState('straightsPricing.contactModalId', null);
  const [alertDialog, setAlertDialog] = useState({ isOpen: false, message: '', type: 'info' });
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, message: '', onConfirm: null });
  const [notesDialog, setNotesDialog] = useState({ isOpen: false, notes: '', title: '' });

  const [filters, setFilters] = usePersistedState('straightsPricing.filters', {
    search: '',
    period: '',
    supplier: '',
    delivery_terms: '',
    limit: '50',
    showArchived: false,
    // CONTRACTED toggle — on by default. When OFF, hide any straights
    // parent row where the parent contract_number is set OR any of its
    // months carry a supplier_contract_number / contract_numbers.
    showContracted: true
  });
  const [searchFields, setSearchFields] = usePersistedState('straightsPricing.searchFields', ALL_SEARCH_KEYS);
  const [sorting, setSorting] = usePersistedState('straightsPricing.sorting', { field: '', direction: 'asc' });
  const [pagination, setPagination] = usePersistedState('straightsPricing.pagination', { page: 1, limit: 50, total: 0, totalPages: 0 });

  useSlideOutButton([selectedIds.length, showModal]);

  // Drag-to-resize columns on the outer Straights table (9 columns).
  const { tableRef, ResizeHandle, ColGroup, ResetButton } = useResizableColumns('straightsPricing', 10);

  // --- data load -----------------------------------------------------------
  const loadContracts = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/pricing/straights-contracts`, { credentials: 'include' });
      const data = await res.json();
      setContracts(data.data || []);
    } catch (err) {
      console.error('Error loading straights contracts:', err);
      setContracts([]);
    } finally {
      setLoading(false);
    }
  };

  const loadPeriods = async () => {
    try {
      const res = await fetch(`${API_URL}/api/pricing/periods?category_id=1&is_active=all`, { credentials: 'include' });
      const data = await res.json();
      setPeriods(Array.isArray(data) ? data : (data.data || []));
    } catch (err) {
      console.error('Error loading periods:', err);
    }
  };

  useEffect(() => { loadContracts(); loadPeriods(); }, []);

  // For each month row of a contract, return which period(s) it falls inside.
  // Compare numerically by (year, month) extracted from the period's date
  // string — building Date objects causes timezone drift around BST/GMT
  // boundaries (e.g. Oct 1 local-midnight becomes Sep 30 23:00 UTC, which
  // sits before a UTC-parsed period start of 2025-10-01 and falsely fails
  // the boundary check).
  const ymFromDateStr = (s) => {
    if (!s) return null;
    const str = typeof s === 'string' ? s : new Date(s).toISOString();
    const [y, m] = str.slice(0, 10).split('-').map(n => parseInt(n, 10));
    return [y, m];
  };
  const periodsForMonth = (year, month) => {
    const ymVal = year * 100 + month;
    return periods.filter(p => {
      const start = ymFromDateStr(p.start_date);
      const end = ymFromDateStr(p.end_date);
      if (!start || !end) return false;
      const startVal = start[0] * 100 + start[1];
      const endVal = end[0] * 100 + end[1];
      return ymVal >= startVal && ymVal <= endVal;
    });
  };

  // All periods a contract touches (via its months).
  const periodsForContract = (c) => {
    const seen = new Map();
    for (const m of c.months || []) {
      const ps = periodsForMonth(m.year, m.month);
      for (const p of ps) if (!seen.has(p.id)) seen.set(p.id, p);
    }
    return Array.from(seen.values()).sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
  };

  // True when today's local-date string falls within the period's date range.
  // Uses string comparison on the YYYY-MM-DD slice to side-step the same BST/UTC
  // drift that bit period-month detection earlier.
  const todayStr = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  })();
  const isCurrentPeriod = (p) => {
    if (!p || !p.start_date || !p.end_date) return false;
    const start = (typeof p.start_date === 'string' ? p.start_date : new Date(p.start_date).toISOString()).slice(0, 10);
    const end = (typeof p.end_date === 'string' ? p.end_date : new Date(p.end_date).toISOString()).slice(0, 10);
    return todayStr >= start && todayStr <= end;
  };

  useEffect(() => {
    const newLimit = parseInt(filters.limit, 10) || 50;
    setPagination(prev => ({ ...prev, limit: newLimit, page: 1, totalPages: Math.ceil(prev.total / newLimit) }));
  }, [filters.limit]);

  // --- derivations --------------------------------------------------------
  const filteredAndSorted = useMemo(() => {
    let list = contracts;
    if (filters.search) {
      const activeSearchFields = SEARCH_FIELDS.filter(f => searchFields.includes(f.key));
      list = list.filter(c => matchesAllTerms(activeSearchFields.map(f => f.get(c)), filters.search));
    }
    if (filters.supplier) {
      list = list.filter(c => c.supplier_contact_id?.toString() === filters.supplier);
    }
    if (filters.delivery_terms) {
      list = list.filter(c => c.delivery_terms === filters.delivery_terms);
    }
    if (!filters.showArchived) {
      list = list.filter(c => !c.is_archived);
    }
    if (!filters.showContracted) {
      // Drop any parent whose contract_number is set OR any of whose
      // months has a supplier_contract_number / non-empty contract_numbers.
      list = list.filter(c => {
        if (c.contract_number && String(c.contract_number).trim()) return false;
        const monthsHaveContract = (c.months || []).some(m =>
          (m.supplier_contract_number && String(m.supplier_contract_number).trim())
          || (Array.isArray(m.contract_numbers) && m.contract_numbers.some(s => s && String(s).trim()))
        );
        return !monthsHaveContract;
      });
    }
    if (filters.period) {
      const period = periods.find(p => p.id.toString() === filters.period);
      if (period) {
        const start = ymFromDateStr(period.start_date);
        const end = ymFromDateStr(period.end_date);
        if (start && end) {
          const startVal = start[0] * 100 + start[1];
          const endVal = end[0] * 100 + end[1];
          list = list.filter(c => (c.months || []).some(m => {
            const ymVal = m.year * 100 + m.month;
            return ymVal >= startVal && ymVal <= endVal;
          }));
        }
      }
    }
    if (sorting.field) {
      list = [...list].sort((a, b) => {
        let av = a[sorting.field];
        let bv = b[sorting.field];
        if (sorting.field === 'total_quantity') { av = sumQty(a.months); bv = sumQty(b.months); }
        // Periods aren't a column here — they're derived from the contract's
        // months. Sort on the earliest one's start_date so the column reads
        // chronologically rather than alphabetically.
        if (sorting.field === 'period_names') {
          av = periodSortKey(periods, periodsForContract(a));
          bv = periodSortKey(periods, periodsForContract(b));
        }
        av = av ?? '';
        bv = bv ?? '';
        if (typeof av === 'string') av = av.toLowerCase();
        if (typeof bv === 'string') bv = bv.toLowerCase();
        if (av < bv) return sorting.direction === 'asc' ? -1 : 1;
        if (av > bv) return sorting.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return list;
    // `periods` is a dep because the Period sort resolves names to start_dates
    // through it — without it the first sort after load uses an empty lookup.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contracts, filters, sorting, searchFields, periods]);

  useEffect(() => {
    setPagination(prev => ({
      ...prev,
      page: 1,
      total: filteredAndSorted.length,
      totalPages: Math.ceil(filteredAndSorted.length / prev.limit)
    }));
  }, [filteredAndSorted.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const paginated = useMemo(() =>
    filteredAndSorted.slice((pagination.page - 1) * pagination.limit, pagination.page * pagination.limit),
    [filteredAndSorted, pagination.page, pagination.limit]);

  const supplierOptions = useMemo(() => {
    const seen = new Map();
    for (const c of contracts) {
      if (c.supplier_contact_id && !seen.has(c.supplier_contact_id)) {
        seen.set(c.supplier_contact_id, c.supplier_name || c.supplier_account_no || `Supplier ${c.supplier_contact_id}`);
      }
    }
    return [
      { value: '', label: 'All suppliers' },
      ...Array.from(seen.entries()).map(([id, label]) => ({ value: id.toString(), label }))
    ];
  }, [contracts]);

  // Month sub-rows are a second, distinct table, so they get their own hook —
  // each row carries its parent contract because the month actions act on it.
  const monthRows = useMemo(
    () => paginated.flatMap(c => (c.months || []).map(m => ({ ...m, contract: c }))),
    [paginated]
  );

  // The context-menu hook works in Sets; this page keeps selection as an array.
  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const ctxContracts = useTableContextMenu({
    rows: paginated,
    selectedIds: selectedIdSet,
    getRowId: (c) => c.id
  });

  // Month rows have no selection of their own — row actions only.
  const ctxMonths = useTableContextMenu({
    rows: monthRows,
    getRowId: (m) => `month-${m.id}`
  });

  // --- handlers -----------------------------------------------------------
  const handleFilterChange = (field, value) => setFilters(prev => ({ ...prev, [field]: value }));

  const handleSort = (field) => setSorting(prev => ({
    field,
    direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
  }));

  const toggleExpand = (id) => setExpandedIds(prev => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  // Expand-all / collapse-all over the paginated set. All straights rows
  // are individually expandable so this is just "every id on this page".
  const toggleExpandAll = () => {
    setExpandedIds(prev =>
      paginated.length > 0 && paginated.every(c => prev.has(c.id))
        ? new Set()
        : new Set(paginated.map(c => c.id))
    );
  };

  const toggleSelect = (id) => setSelectedIds(prev =>
    prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
  );
  const toggleSelectAll = () => {
    if (selectedIds.length === paginated.length) setSelectedIds([]);
    else setSelectedIds(paginated.map(c => c.id));
  };

  const handleAddNew = () => { setSelectedContract(null); setModalMode('edit'); setShowModal(true); };

  const handleClearFilters = () => {
    setFilters(prev => ({ ...CLEARED_FILTERS, limit: prev.limit || '50' }));
    setSearchFields(ALL_SEARCH_KEYS);
    setSorting({ field: '', direction: 'asc' });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Any header filter on lights the Clear Filters button.
  const headerFilters = activeHeaderFilters(filters, CLEARED_FILTERS, { labels: FILTER_LABELS });
  const filtersApplied = headerFilters.count > 0;
  const handleEdit = (contract) => { setSelectedContract(contract); setModalMode('edit'); setShowModal(true); };
  const handleView = (contract) => { setSelectedContract(contract); setModalMode('view'); setShowModal(true); };

  const handleArchive = async (contract) => {
    const archiving = !contract.is_archived;
    try {
      const res = await fetch(`${API_URL}/api/pricing/straights-contracts/${contract.id}/archive`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ is_archived: archiving })
      });
      if (!res.ok) throw new Error(`${archiving ? 'Archive' : 'Unarchive'} failed`);
      loadContracts();
    } catch (e) {
      setAlertDialog({ isOpen: true, message: e.message, type: 'error' });
    }
  };

  const handleToggleHold = async (contract) => {
    try {
      await fetch(`${API_URL}/api/pricing/straights-contracts/${contract.id}/hold`, { method: 'PATCH', credentials: 'include' });
      loadContracts();
    } catch (e) { console.error('Error toggling hold:', e); }
  };

  const handleRefreshCost = async (contract) => {
    try {
      const res = await fetch(`${API_URL}/api/pricing/straights-contracts/${contract.id}/refresh-cost`, {
        method: 'POST', credentials: 'include'
      });
      const data = await res.json();
      if (data.skipped) {
        setAlertDialog({ isOpen: true, message: data.reason || 'No purchase_unit_price on inventory item', type: 'warning' });
        return;
      }
      await loadContracts();
      setAlertDialog({ isOpen: true, message: `Cost refreshed to ${formatPrice(data.newCost ?? 0)} on ${data.monthsUpdated} month${data.monthsUpdated !== 1 ? 's' : ''}`, type: 'success' });
    } catch (error) {
      console.error('Error refreshing cost:', error);
      setAlertDialog({ isOpen: true, message: 'Failed to refresh cost', type: 'error' });
    }
  };

  const handleBulkRefreshCost = () => {
    if (selectedIds.length === 0) return;
    setConfirmDialog({
      isOpen: true,
      message: `Refresh cost for ${selectedIds.length} straights contract${selectedIds.length !== 1 ? 's' : ''} from the current inventory cost? Every month on each contract will be updated.`,
      onConfirm: async () => {
        setConfirmDialog({ isOpen: false, message: '', onConfirm: null });
        try {
          const res = await fetch(`${API_URL}/api/pricing/straights-contracts/refresh-costs`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ ids: selectedIds })
          });
          const data = await res.json();
          setSelectedIds([]);
          await loadContracts();
          const updated = data.updated?.length || 0;
          const skipped = data.skipped?.length || 0;
          const failed = data.failed?.length || 0;
          const parts = [`${updated} refreshed`];
          if (skipped > 0) parts.push(`${skipped} skipped (no inventory cost)`);
          if (failed > 0) parts.push(`${failed} failed`);
          setAlertDialog({ isOpen: true, message: parts.join(', '), type: failed > 0 ? 'warning' : 'success' });
        } catch (error) {
          console.error('Error bulk refreshing straights costs:', error);
          setAlertDialog({ isOpen: true, message: 'Failed to bulk refresh costs', type: 'error' });
        }
      }
    });
  };

  // Patch only the contract that was just edited / created instead of
  // reloading the whole list. Falls back to a full reload if the modal
  // didn't give us an id.
  const refreshRow = async ({ straightsContractId } = {}) => {
    if (!straightsContractId) return loadContracts();
    try {
      const params = new URLSearchParams({ id: straightsContractId });
      const res = await fetch(`${API_URL}/api/pricing/straights-contracts?${params}`, { credentials: 'include' });
      const data = await res.json();
      const refreshed = (data.data || [])[0];
      if (!refreshed) return loadContracts();
      setContracts(prev => {
        const existing = prev.some(c => c.id === refreshed.id);
        return existing
          ? prev.map(c => c.id === refreshed.id ? refreshed : c)
          : [...prev, refreshed];
      });
    } catch (err) {
      console.error('Error refreshing straights row:', err);
      await loadContracts();
    }
  };

  const handleSave = async (savedInfo = {}) => {
    setShowModal(false);
    await refreshRow(savedInfo);
  };

  const changePage = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, page: newPage }));
    }
  };

  // --- render helpers -----------------------------------------------------
  const renderCustomerCell = (c) => {
    // Aggregate fulfilment status across the contract's months. Each month has
    // its own period (year + month → last day of month) so a contract can be
    // UNFULFILLED for early months while UTILISE for later ones — show the
    // worst status on the parent row (priority: UNFULFILLED > UTILISE >
    // FULFILLED > none). months[].remaining_quantity is the already-calculated
    // "qty remaining" — convert it back to an ordered figure for the helper.
    // A contract-level override beats anything else (rendered as "manual").
    const monthsForStatus = (c.months || []).map(m => ({
      quantity: m.quantity,
      ordered_quantity: (parseFloat(m.quantity) || 0) - (parseFloat(m.remaining_quantity) || 0),
      period_end: new Date(m.year, m.month, 0).toISOString().slice(0, 10),
      is_fulfilled_override: m.is_fulfilled_override
    }));
    return (
      <ContractCustomerCell
        accountNo={c.account_no}
        accountId={c.account_id}
        onAccountClick={() => setSelectedContactId(c.account_id)}
        name={c.company_name}
        onNameClick={() => { if (c.account_id) setSelectedContactId(c.account_id); }}
        flags={{ hold: c.is_on_hold, draft: c.is_draft, archived: c.is_archived }}
        status={c.is_fulfilled_override ? null : aggregateFulfilmentStatus(monthsForStatus)}
        manualFulfilled={c.is_fulfilled_override}
        linkedPoCount={(c.months || []).reduce((s, m) => s + (parseInt(m.linked_po_count) || 0), 0)}
        onLinkedClick={() => handleView(c)}
        linkedScope="all months"
        address={c.address_line}
        scopeKind={c.scope_kind}
      />
    );
  };

  /**
   * Period bounds for a straights contract, taken from the row's OWN months
   * rather than from the period filter.
   *
   * The printed document must cover exactly the months shown against that row.
   * Reading the filter instead would print a different span whenever the filter
   * is cleared, or nothing at all when it was never set.
   */
  const monthBounds = (c) => {
    const months = c.months || [];
    if (!months.length) return null;
    const keys = months.map(m => m.year * 100 + m.month).sort((a, b) => a - b);
    const first = keys[0], last = keys[keys.length - 1];
    const pad = (n) => String(n).padStart(2, '0');
    return {
      start: `${Math.floor(first / 100)}-${pad(first % 100)}-01`,
      // Day 0 of the following month is the last day of this one.
      end: new Date(Math.floor(last / 100), (last % 100), 0).toISOString().slice(0, 10)
    };
  };

  const printStraightsContracts = (rowsIn) => {
    const rows = rowsIn.filter(c => (c.months || []).length && !c.is_holding_product); // a holding contract has no product to print
    if (!rows.length) {
      setAlertDialog({ isOpen: true, message: 'Nothing to print — these rows have no monthly pricing.', type: 'info' });
      return;
    }
    // One request per distinct period span, since the endpoint takes a single
    // start/end. In practice every row shares the period being looked at, so
    // this is one call; it only splits if the selection genuinely spans
    // different date ranges.
    const bySpan = new Map();
    for (const c of rows) {
      const b = monthBounds(c);
      if (!b) continue;
      const key = `${b.start}|${b.end}`;
      if (!bySpan.has(key)) bySpan.set(key, { ...b, rows: [] });
      bySpan.get(key).rows.push({ accountNo: c.account_no, itemCode: c.product_code });
    }
    for (const span of bySpan.values()) {
      openStraightsContractPdf(span.rows, {
        start: span.start,
        end: span.end,
        onError: (message, type) => setAlertDialog({ isOpen: true, message, type: type || 'error' })
      });
    }
  };

  // Shared by the row's kebab menu and its right-click context menu.
  // Mark / unmark a straights contract fulfilled from the row menus.
  const handleSetFulfilled = async (c, isFulfilled) => {
    try {
      await setStraightsContractFulfilled(c.id, isFulfilled);
      loadContracts();
    } catch (e) {
      setAlertDialog({ isOpen: true, message: `Failed to ${isFulfilled ? 'mark' : 'unmark'} the contract fulfilled: ${e.message}`, type: 'error' });
    }
  };

  const buildRowActions = (c) => [
    { key: 'view', icon: 'visibility', label: 'View', onClick: () => handleView(c) },
    {
      key: 'contract',
      icon: 'description',
      label: 'Print contract',
      // A holding contract has no product to put on the page yet.
      disabled: !!c.is_holding_product,
      title: c.is_holding_product
        ? 'Holding product — choose the item before printing the contract'
        : 'Two-page sales contract with terms overleaf — print double-sided',
      onClick: () => printStraightsContracts([c])
    },
    { key: 'orders', icon: 'playlist_add', label: 'Add orders…', title: 'The orders counted against this contract\'s months — add any of this customer\'s orders by hand, or take one off', onClick: () => setOrdersFor({ set: { kind: 'straights', id: c.id }, title: `${c.company_name} — ${c.product_name || c.item_name || 'straights contract'}` }) },
    { key: 'notes', icon: 'sticky_note_2', label: c.notes ? 'View Notes' : 'No Notes', disabled: !c.notes, onClick: () => setNotesDialog({ isOpen: true, notes: c.notes, title: `${c.company_name} — Notes` }) },
    { key: 'hold', icon: c.is_on_hold ? 'play_circle' : 'pause_circle', label: c.is_on_hold ? 'Resume' : 'Hold', className: c.is_on_hold ? 'action-unhold' : 'action-hold', onClick: () => handleToggleHold(c) },
    fulfilledAction({ fulfilled: !!c.is_fulfilled_override, onToggle: () => handleSetFulfilled(c, !c.is_fulfilled_override) }),
    { key: 'edit', icon: 'edit', label: 'Edit', onClick: () => handleEdit(c) },
    { key: 'refresh', icon: 'sync', label: 'Refresh cost', onClick: () => handleRefreshCost(c) },
    { key: 'archive', icon: c.is_archived ? 'unarchive' : 'archive', label: c.is_archived ? 'Unarchive' : 'Archive', onClick: () => handleArchive(c) },
  ];

  // The month sub-row's context menu: View, and the orders counted against
  // that month (28 Oct 2026).
  const buildMonthActions = (m) => [
    { key: 'view', icon: 'visibility', label: 'View', onClick: () => handleView(m.contract) },
    { key: 'orders', icon: 'playlist_add', label: 'Add orders…', title: 'The orders counted against this month — add any of this customer\'s orders by hand, or take one off', onClick: () => setOrdersFor({ set: { kind: 'monthly', ids: [m.id] }, title: `${m.contract?.company_name || ''} — ${m.contract?.product_name || 'straights'} (month)` }) },
  ];

  const renderActions = (c) => (
    <RowActionsMenu actions={buildRowActions(c)} />
  );

  const allPageSelected = paginated.length > 0 && selectedIds.length === paginated.length;
  const allPageExpanded = paginated.length > 0 && paginated.every(c => expandedIds.has(c.id));

  // Shared by both tables' context menus.
  const genericActions = [
    { key: 'reload', icon: 'refresh', label: 'Reload contracts', onClick: loadContracts },
    { key: 'clear-filters', icon: 'filter_alt_off', label: 'Clear filters', onClick: handleClearFilters },
    {
      key: 'expand-all',
      icon: allPageExpanded ? 'unfold_less' : 'unfold_more',
      label: allPageExpanded ? 'Collapse all' : 'Expand all',
      disabled: paginated.length === 0,
      onClick: toggleExpandAll
    },
    {
      key: 'select-all',
      icon: allPageSelected ? 'deselect' : 'select_all',
      label: allPageSelected ? 'Clear selection' : 'Select all on this page',
      disabled: paginated.length === 0,
      onClick: toggleSelectAll
    },
    {
      key: 'show-archived',
      icon: 'archive',
      label: 'Show archived',
      active: !!filters.showArchived,
      onClick: () => handleFilterChange('showArchived', !filters.showArchived)
    },
    {
      key: 'show-contracted',
      icon: 'description',
      label: 'Show contracted',
      active: !!filters.showContracted,
      onClick: () => handleFilterChange('showContracted', !filters.showContracted)
    }
  ];

  // --- render -------------------------------------------------------------
  return (
    <div className="contract-pricing straights-pricing">
      <div className="search-header">
        <div className="workflow-info" style={{ gridColumn: '1' }}>
          <span className="info">Manage monthly straights contract pricing</span>
        </div>
        <div className="search-header-inner" style={{ gridColumn: '2' }}>
          <div className="buttons slideout">
            <div>
              <button
                className={clearFiltersClass(filtersApplied)}
                onClick={handleClearFilters}
                title={filtersApplied ? 'Clear all filters' : 'No filters applied'}
              >
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">filter_alt_off</span>
                </div>
                <span className="btn-label">Clear Filters</span>
              </button>
            </div>
            <RecalculateQuantitiesButton
              user={user}
              onDone={() => loadContracts()}
              onAlert={setAlertDialog}
            />
            <RefreshAutoLinksButton
              user={user}
              periods={periods}
              onDone={() => loadContracts()}
              onAlert={setAlertDialog}
            />
            <div>
              <button className="btn-slideout" onClick={handleAddNew}>
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">add</span>
                </div>
                <span className="btn-label">Add New</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="search-filters" style={{ paddingBottom: 'var(--spacing-md)' }}>
        <div className="filter-row" style={{ gridTemplateColumns: '1fr 150px 135px 195px 150px 60px 85px 70px' }}>
          <div className="filter-group">
            <label>
              Search
              <SearchHelp fields={['customer', 'account no.', 'product', 'product code', 'contract no. (ours and the supplier\'s)', 'or the fields chosen in Search In']} />
            </label>
            <Input
              type="text"
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              placeholder='Terms, or "exact phrase"…'
              icon="search"
            />
          </div>
          <div className="filter-group">
            <label>Search In</label>
            <MultiSelectDropdown
              options={SEARCH_FIELD_OPTIONS}
              value={searchFields}
              onChange={setSearchFields}
              summarize={searchFieldsSummary}
              hideTags
              placeholder="Search All"
            />
          </div>
          <div className="filter-group">
            <label>Contract Period</label>
            <Dropdown
              value={filters.period}
              onChange={(v) => handleFilterChange('period', v)}
              options={[
                { value: '', label: 'All Periods' },
                ...periods.map(p => ({ value: p.id.toString(), label: p.name }))
              ]}
              placeholder="All Periods"
            />
          </div>
          <div className="filter-group">
            <label>Supplier</label>
            <Dropdown value={filters.supplier} onChange={(v) => handleFilterChange('supplier', v)} options={supplierOptions} placeholder="All suppliers" />
          </div>
          <div className="filter-group">
            <label>Delivery</label>
            <Dropdown
              value={filters.delivery_terms}
              onChange={(v) => handleFilterChange('delivery_terms', v)}
              options={[
                { value: '', label: 'All' },
                { value: 'Direct Delivery', label: 'Direct Delivery' },
                { value: 'Delivery', label: 'Delivery' },
                { value: 'Customer Collection', label: 'Customer Collection' }
              ]}
            />
          </div>
          <div className="filter-group">
            <label>Archived</label>
            <div className="checkbox-container">
              <div className="check light">
                <input
                  id="show-archived-straights"
                  type="checkbox"
                  checked={!!filters.showArchived}
                  onChange={(e) => handleFilterChange('showArchived', e.target.checked)}
                />
                <label htmlFor="show-archived-straights"></label>
              </div>
            </div>
          </div>
          <div className="filter-group">
            <label title="Untick to hide parents whose contract number is set, or where any month carries a supplier contract number.">Contracted</label>
            <div className="checkbox-container">
              <div className="check light">
                <input
                  id="show-contracted-straights"
                  type="checkbox"
                  checked={!!filters.showContracted}
                  onChange={(e) => handleFilterChange('showContracted', e.target.checked)}
                />
                <label htmlFor="show-contracted-straights"></label>
              </div>
            </div>
          </div>
          <div className="filter-group">
            <label>Per Page</label>
            <Dropdown
              value={filters.limit}
              onChange={(v) => handleFilterChange('limit', v)}
              options={[{value:'20',label:'20'},{value:'50',label:'50'},{value:'100',label:'100'},{value:'200',label:'200'}]}
            />
          </div>
        </div>
      </div>

      <div className="table-toolbar">
        <div style={{ display: 'flex', gap: 'var(--spacing-sm)', alignItems: 'center' }}>
          {paginated.length > 0 && (() => {
            const allExpanded = paginated.every(c => expandedIds.has(c.id));
            return (
              <button
                className="btn-secondary btn-sm"
                onClick={toggleExpandAll}
                disabled={loading}
              >
                <span className="material-symbols-rounded">{allExpanded ? 'unfold_less' : 'unfold_more'}</span>
                {allExpanded ? 'Collapse All' : 'Expand All'}
              </button>
            );
          })()}
          <ResetButton />
        </div>
        {pagination && pagination.total > 0 && (
          <PaginationControls pagination={pagination} onPageChange={changePage} />
        )}
      </div>

      {selectedIds.length > 0 && (
        <div className="bulk-actions-bar">
          <div className="bulk-actions-info">
            <span>{selectedIds.length} contract{selectedIds.length !== 1 ? 's' : ''} selected</span>
          </div>
          <div className="bulk-actions-buttons slideout">
            <div>
              <button
                className="btn-slideout"
                onClick={() => printStraightsContracts(
                  filteredAndSorted.filter(c => selectedIds.includes(c.id))
                )}
              >
                <div className="icon-wrapper"><span className="material-symbols-rounded">description</span></div>
                <span className="btn-label">Print Contracts</span>
              </button>
            </div>
            <div>
              <button className="btn-slideout" onClick={handleBulkRefreshCost}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">sync</span></div>
                <span className="btn-label">Refresh Costs</span>
              </button>
            </div>
            <div>
              <button data-read-safe className="btn-slideout btn-danger" onClick={() => setSelectedIds([])}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
                <span className="btn-label">Clear</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="table-container">
        {loading ? (
          <LoadingSpinner />
        ) : (
          <table className="data-table resizable-table" ref={tableRef} onContextMenu={ctxContracts.onTableContextMenu}>
            <ColGroup />
            <thead>
              <tr>
                <th className="checkbox-col">
                  <label className="tick-check-label" style={{ justifyContent: 'center', gap: '0' }}>
                    <input type="checkbox" className="tick-check-input" id="select-all-straights"
                      checked={paginated.length > 0 && selectedIds.length === paginated.length}
                      onChange={toggleSelectAll} />
                    <label htmlFor="select-all-straights" className="tick-check">
                      <svg width="16px" height="16px" viewBox="0 0 18 18"><path d="M1,9 L1,3.5 C1,2 2,1 3.5,1 L14.5,1 C16,1 17,2 17,3.5 L17,14.5 C17,16 16,17 14.5,17 L3.5,17 C2,17 1,16 1,14.5 L1,9 Z"></path><polyline points="1 9 7 14 15 4"></polyline></svg>
                    </label>
                  </label>
                  <ResizeHandle colIndex={0} />
                </th>
                <th className="expand-cell"><ResizeHandle colIndex={1} /></th>
                <th className="sortable" onClick={() => handleSort('company_name')}>
                  Customer {sorting.field === 'company_name' && (sorting.direction === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={2} />
                </th>
                <th className="sortable" onClick={() => handleSort('product_name')}>
                  Product {sorting.field === 'product_name' && (sorting.direction === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={3} />
                </th>
                <th className="sortable" onClick={() => handleSort('contract_number')}>
                  Contract {sorting.field === 'contract_number' && (sorting.direction === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={4} />
                </th>
                <th>Supplier<ResizeHandle colIndex={5} /></th>
                <th>Delivery<ResizeHandle colIndex={6} /></th>
                <th>Dock<ResizeHandle colIndex={7} /></th>
                <th style={{ textAlign: 'center' }} className="sortable" onClick={() => handleSort('period_names')}>
                  Period(s) {sorting.field === 'period_names' && (sorting.direction === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={8} />
                </th>
                <th className="actions-col"><ResizeHandle colIndex={9} /></th>
              </tr>
            </thead>
            <tbody>
              {paginated.length === 0 ? (
                <tr><td colSpan="10" className="text-center">No contracts found</td></tr>
              ) : paginated.map((c) => {
                const months = (c.months || []).slice().sort((a, b) => (a.year - b.year) || (a.month - b.month));
                const isExpanded = expandedIds.has(c.id);
                const contractPeriods = periodsForContract(c);

                return (
                  <React.Fragment key={c.id}>
                    <tr
                      className={`expandable-row ${isExpanded ? 'expanded' : ''} ${c.is_on_hold ? 'on-hold' : ''} ${c.is_archived ? 'archived' : ''}`}
                      style={{ cursor: 'pointer' }}
                      onClick={() => toggleExpand(c.id)}
                      onContextMenu={ctxContracts.onRowContextMenu(c)}
                    >
                      <td className="checkbox-col" onClick={(e) => e.stopPropagation()}>
                        <label className="tick-check-label" style={{ justifyContent: 'center', gap: '0' }}>
                          <input type="checkbox" className="tick-check-input" id={`select-straights-${c.id}`} checked={selectedIds.includes(c.id)} onChange={() => toggleSelect(c.id)} />
                          <label htmlFor={`select-straights-${c.id}`} className="tick-check">
                            <svg width="16px" height="16px" viewBox="0 0 18 18"><path d="M1,9 L1,3.5 C1,2 2,1 3.5,1 L14.5,1 C16,1 17,2 17,3.5 L17,14.5 C17,16 16,17 14.5,17 L3.5,17 C2,17 1,16 1,14.5 L1,9 Z"></path><polyline points="1 9 7 14 15 4"></polyline></svg>
                          </label>
                        </label>
                      </td>
                      <td className="expand-cell">
                        <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>▼</span>
                      </td>
                      <td>{renderCustomerCell(c)}</td>
                      <td>
                        <ProductCell productCode={c.product_code} productName={c.product_name} isHolding={c.is_holding_product} />
                      </td>
                      <td>{c.contract_number
                        ? <span className="contract-chip" title="Our contract number (Excel Agri ↔ customer)">{c.contract_number}</span>
                        : <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                      <td>{c.supplier_name || c.supplier_account_no || '-'}</td>
                      <td>{c.delivery_terms || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                      <td>{c.dock || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                      <td style={{ textAlign: 'center' }}>
                        {contractPeriods.map(p => (
                          <React.Fragment key={p.id}>
                            <span className={`period-badge ${p.name.toLowerCase().startsWith('winter') ? 'winter' : 'summer'}${isPastPeriod(p) ? ' past-period' : ''}`}>{p.name}</span>
                            {isCurrentPeriod(p) && (
                              <span className="material-symbols-rounded period-current-icon" title="Current period">check_circle</span>
                            )}
                          </React.Fragment>
                        ))}
                      </td>
                      <td className="actions-col" onClick={(e) => e.stopPropagation()}>
                        {renderActions(c)}
                      </td>
                    </tr>

                    <tr className={`expanded-content ${isExpanded ? 'expanded' : ''}`}>
                      <td colSpan="10">
                        <div className="line-items-container">
                          <table className="line-items-table">
                            <thead>
                              <tr>
                                <th></th>
                                <th>Supplier Contract</th>
                                <th>Date</th>
                                <th className="text-right">Quantity (Remaining)</th>
                                <th className="text-right">Price (Cost)</th>
                                <th></th>
                              </tr>
                            </thead>
                            <tbody>
                              {months.length === 0 ? (
                                <tr><td colSpan="6" style={{ color: 'var(--text-muted)', textAlign: 'center' }}>No months</td></tr>
                              ) : months.map((m) => {
                                const qty = parseFloat(m.quantity) || 0;
                                const rem = parseFloat(m.remaining_quantity);
                                const monthLinkedPos = parseInt(m.linked_po_count || 0);
                                return (
                                  <tr key={`month-${m.id}`} onContextMenu={ctxMonths.onRowContextMenu({ ...m, contract: c })}>
                                    <td style={{ textAlign: 'right' }}>
                                      <span className="sub-row-badges" style={{ justifyContent: 'flex-end' }}>
                                        <LinkedPosBadge count={monthLinkedPos} onClick={() => handleView(c)} />
                                      </span>
                                    </td>
                                    <td>{m.supplier_contract_number
                                      ? <span
                                          style={{
                                            display: 'inline-flex', alignItems: 'center',
                                            padding: '2px 8px',
                                            fontSize: 'var(--font-xs)', fontWeight: 'var(--font-semibold)',
                                            background: 'rgba(var(--color-primary-rgb), 0.12)',
                                            color: 'var(--color-primary)',
                                            borderRadius: 'var(--radius-sm)',
                                            letterSpacing: '0.2px', lineHeight: '1.4'
                                          }}
                                          title="Supplier contract number"
                                        >{m.supplier_contract_number}</span>
                                      : <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                                    <td style={{ whiteSpace: 'nowrap' }}>{fmtMonthYear(m.year, m.month)}</td>
                                    <td className="text-right">
                                      <span style={{ marginRight: '5px' }} className='quant'>{qty ? formatQuantity(qty) : '-'}</span>
                                      {qty > 0 && (
                                        <span className='rem' style={{ fontSize: 'var(--font-xs)', color: rem < 0 ? 'var(--color-error)' : rem === 0 ? 'var(--text-secondary)' : 'var(--text-muted)' }}>({formatQuantity(rem ?? 0)})</span>
                                      )}
                                    </td>
                                    <td className="text-right">
                                      <span style={{ marginRight: '5px' }} className="price">{m.price != null && m.price !== '' ? formatPrice(m.price) : '-'}</span>
                                      {m.cost != null && m.cost !== '' && (
                                        <span className="cost">({formatPrice(m.cost)})</span>
                                      )}
                                    </td>
                                    <td style={{ textAlign: 'center' }}>
                                      <button
                                        className="btn-icon"
                                        onClick={(e) => { e.stopPropagation(); handleView(c); }}
                                        title="View"
                                      >
                                        <span className="material-symbols-rounded">visibility</span>
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {ordersFor && (
        <ContractOrdersModal
          set={ordersFor.set}
          title={ordersFor.title}
          onClose={() => { setOrdersFor(null); loadContracts(); }}
        />
      )}

      <TableContextMenu
        ctx={ctxContracts}
        rowActions={buildRowActions}
        bulkActions={(ids) => [
          {
            key: 'bulk-refresh',
            icon: 'sync',
            label: `Refresh cost on ${ids.size} contracts`,
            onClick: handleBulkRefreshCost
          }
        ]}
        actions={genericActions}
      />

      <TableContextMenu
        ctx={ctxMonths}
        rowActions={buildMonthActions}
        actions={genericActions}
      />

      {showModal && (
        <StraightsContractModal
          contract={selectedContract}
          mode={modalMode}
          onClose={() => { setShowModal(false); loadContracts(); }}
          onSave={handleSave}
        />
      )}

      <AlertDialog
        isOpen={alertDialog.isOpen}
        message={alertDialog.message}
        type={alertDialog.type}
        onClose={() => setAlertDialog({ ...alertDialog, isOpen: false })}
      />

      <AlertDialog
        isOpen={notesDialog.isOpen}
        title={notesDialog.title}
        message={notesDialog.notes}
        type="info"
        onClose={() => setNotesDialog({ isOpen: false, notes: '', title: '' })}
      />

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
      />

      {selectedContactId && ReactDOM.createPortal(
        <ContactModal contactId={selectedContactId} onClose={() => setSelectedContactId(null)} />,
        document.body
      )}
    </div>
  );
}

export default StraightsPricing;
